/**
 * TemplateFormRenderer - Componente para renderizar formulários dinâmicos
 * 
 * Utiliza @rjsf (React JSON Schema Form) para renderizar formulários
 * baseados em JSON Schema com suporte a campos condicionais.
 */

import React, { useMemo, useCallback } from 'react'
import Form from '@rjsf/core'
import type { RJSFSchema, UiSchema, RegistryWidgetsType, FieldTemplateProps, ObjectFieldTemplateProps, ArrayFieldTemplateProps, ArrayFieldItemTemplateProps } from '@rjsf/utils'
import validator from '@rjsf/validator-ajv8'
import type { TemplateSchema } from '../types'
import { cn } from '../../../utils/formatter'
import { Textarea } from '../../../components'
import { RichTextDisplay } from '../../../components/RichTextDisplay'
import { HiCalendar, HiEnvelope, HiCheck } from 'react-icons/hi2'
import {
  CPFWidget,
  FornecedorSelectorWidget,
  CargoSelectorWidget,
  UnidadeSelectorWidget,
  UserSelectorWidget,
} from './CustomWidgets'

// Estilos Tailwind para os componentes do formulário
const inputClasses = cn(
  'w-full px-3 py-2 text-sm rounded-lg border transition-colors duration-200',
  'bg-white dark:bg-neutral-800',
  'border-gray-300 dark:border-neutral-600',
  'text-gray-900 dark:text-gray-100',
  'placeholder:text-gray-400 dark:placeholder:text-gray-500',
  'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
  'disabled:opacity-50 disabled:cursor-not-allowed'
)

const selectClasses = cn(
  inputClasses,
  'appearance-none cursor-pointer',
  'bg-[url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3e%3cpath stroke=%27%236b7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3e%3c/svg%3e")] bg-no-repeat bg-[right_0.5rem_center] bg-[length:1.5em_1.5em] pr-10'
)

const textareaClasses = cn(
  inputClasses,
  'resize-none min-h-[100px]'
)

const labelClasses = cn(
  'block text-sm font-medium mb-1.5',
  'text-gray-700 dark:text-gray-300'
)

const descriptionClasses = cn(
  'text-xs text-gray-500 dark:text-gray-400 mt-1'
)

const errorClasses = cn(
  'text-xs text-red-500 dark:text-red-400 mt-1'
)

const requiredClasses = cn(
  'text-red-500 ml-0.5'
)

// Widget customizado para Input de texto
const TextWidget: React.FC<{
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
  autofocus?: boolean
}> = ({ id, value, onChange, disabled, readonly, placeholder, autofocus }) => (
  <input
    id={id}
    type="text"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled || readonly}
    placeholder={placeholder}
    autoFocus={autofocus}
    className={inputClasses}
  />
)

// Widget customizado para Select
const SelectWidget: React.FC<{
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  readonly?: boolean
  options: { enumOptions?: Array<{ value: string; label: string }> }
  placeholder?: string
}> = ({ id, value, onChange, disabled, readonly, options, placeholder }) => {
  const enumOptions = options.enumOptions || []
  
  // Filtrar opções vazias que não sejam a opção de placeholder
  const filteredOptions = enumOptions.filter(opt => opt.value !== '' || opt.label !== '')
  const hasEmptyOption = enumOptions.some(opt => opt.value === '')
  
  return (
    <select
      id={id}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || readonly}
      className={selectClasses}
    >
      {(placeholder || hasEmptyOption) && <option value="">{placeholder || 'Selecione...'}</option>}
      {filteredOptions.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  )
}

// Widget customizado para Date
const DateWidget: React.FC<any> = ({ id, value, onChange, disabled, readonly, placeholder, options }: any) => (
  <div className="relative">
    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
      <HiCalendar className="w-4 h-4" />
    </div>
    <input
      id={id}
      type="date"
      value={value || ''}
      onChange={(e: any) => onChange(e.target.value)}
      disabled={disabled || readonly}
      placeholder={placeholder}
      min={options?.min}
      max={options?.max}
      className={cn(
        inputClasses,
        'pl-9',
        // Fix para date no dark mode
        'dark:[color-scheme:dark]'
      )}
    />
  </div>
)

// Widget customizado para Email
const EmailWidget: React.FC<any> = ({ id, value, onChange, disabled, readonly, placeholder }: any) => (
  <div className="relative">
    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
      <HiEnvelope className="w-4 h-4" />
    </div>
    <input
      id={id}
      type="email"
      value={value || ''}
      onChange={(e: any) => onChange(e.target.value)}
      disabled={disabled || readonly}
      placeholder={placeholder}
      className={cn(inputClasses, 'pl-9')}
    />
  </div>
)

// Widget customizado para Radio (boolean)
const RadioWidget: React.FC<any> = ({ id, value, onChange, disabled, readonly, options }: any) => {
  const isInline = options?.inline ?? true
  
  return (
    <div className={cn('flex gap-3', isInline ? 'flex-row' : 'flex-col')}>
      <label
        htmlFor={`${id}-true`}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all cursor-pointer',
          value === true
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
            : 'border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-neutral-500',
          (disabled || readonly) && 'opacity-50 cursor-not-allowed'
        )}
      >
        <input
          id={`${id}-true`}
          type="radio"
          checked={value === true}
          onChange={() => !disabled && !readonly && onChange(true)}
          disabled={disabled || readonly}
          className="sr-only"
        />
        <div className={cn(
          'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
          value === true
            ? 'border-blue-500 bg-blue-500'
            : 'border-gray-300 dark:border-neutral-600'
        )}>
          {value === true && <HiCheck className="w-3 h-3 text-white" />}
        </div>
        <span className="text-sm font-medium">Sim</span>
      </label>
      
      <label
        htmlFor={`${id}-false`}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all cursor-pointer',
          value === false
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
            : 'border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-neutral-500',
          (disabled || readonly) && 'opacity-50 cursor-not-allowed'
        )}
      >
        <input
          id={`${id}-false`}
          type="radio"
          checked={value === false}
          onChange={() => !disabled && !readonly && onChange(false)}
          disabled={disabled || readonly}
          className="sr-only"
        />
        <div className={cn(
          'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
          value === false
            ? 'border-blue-500 bg-blue-500'
            : 'border-gray-300 dark:border-neutral-600'
        )}>
          {value === false && <HiCheck className="w-3 h-3 text-white" />}
        </div>
        <span className="text-sm font-medium">Não</span>
      </label>
    </div>
  )
}

// Widget customizado para Textarea
const TextareaWidget: React.FC<{
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
  options?: { rows?: number }
}> = ({ id, value, onChange, disabled, readonly, placeholder, options }) => (
  <textarea
    id={id}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled || readonly}
    placeholder={placeholder}
    rows={options?.rows || 4}
    className={textareaClasses}
  />
)

// Widget customizado para Rich Text (Texto Rico)
const RichTextWidget: React.FC<{
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
  options?: { rows?: number }
}> = ({ id, value, onChange, disabled, readonly, placeholder, options: _options }) => {
  // Se readonly, mostrar apenas o conteúdo formatado
  if (readonly || disabled) {
    return (
      <div 
        id={id}
        className={cn(
          'w-full px-3 py-2 text-sm rounded-lg border',
          'bg-gray-50 dark:bg-neutral-900',
          'border-gray-200 dark:border-neutral-700',
          'text-gray-700 dark:text-gray-300',
          'min-h-[100px]'
        )}
      >
        {value ? (
          <RichTextDisplay content={value} className="prose prose-sm max-w-none dark:prose-invert" />
        ) : (
          <span className="text-gray-400 dark:text-gray-500 italic">Sem conteúdo</span>
        )}
      </div>
    )
  }

  return (
    <Textarea
      variant="rich"
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder || 'Digite aqui...'}
      disabled={disabled || false}
    />
  )
}

// Template customizado para campos
const CustomFieldTemplate: React.FC<FieldTemplateProps> = ({
  id,
  label,
  required,
  description,
  rawErrors,
  children,
  schema,
  uiSchema,
}) => {
  // Se for campo null (separador de seção), renderizar como título
  if (schema.type === 'null') {
    return (
      <div className="mt-6 mb-4 first:mt-0">
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-neutral-700 pb-2">
          {schema.title || label}
        </h3>
        {schema.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {/* {schema.description} */}
          </p>
        )}
      </div>
    )
  }

  // Verificar se deve esconder baseado em outro campo
  const hideOnEmpty = uiSchema?.['ui:options']?.hideOnEmpty
  if (hideOnEmpty) {
    // Este campo será controlado pela lógica de campos condicionais do JSON Schema
  }

  // Converter errors para array se necessário
  const errorList = rawErrors || []

  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={id} className={labelClasses}>
          {label}
          {required && <span className={requiredClasses}>*</span>}
        </label>
      )}
      {children}
      {description && (
        <div className={descriptionClasses}>
          {/* {description} */}
          </div>
      )}
      {errorList.length > 0 && (
        <div className={errorClasses}>
          {errorList.map((error: string, index: number) => (
            <span key={index} className="block">{error}</span>
          ))}
        </div>
      )}
    </div>
  )
}

// Template customizado para objetos (container principal)
// Nota: Não renderizamos título/descrição do objeto raiz pois o container externo já fornece esse contexto
const CustomObjectFieldTemplate: React.FC<ObjectFieldTemplateProps> = ({
  properties,
  // title,
  // description,
}) => {
  return (
    <div className="space-y-1">
      {/* {title && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h2>
          {description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {description}
            </p>
          )}
        </div>
      )} */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {properties.map((prop) => {
          // Verificar se é um campo de seção (null type) que deve ocupar linha inteira
          // Usamos type assertion para acessar props de forma segura
          const contentProps = (prop.content as React.ReactElement)?.props as { 
            schema?: { type?: string }
            uiSchema?: { 'ui:widget'?: string }
          } | undefined
          const isNullType = contentProps?.schema?.type === 'null'
          const isRichText = contentProps?.uiSchema?.['ui:widget'] === 'richtext'
          const isTextarea = contentProps?.uiSchema?.['ui:widget'] === 'textarea'
          const isArray = contentProps?.schema?.type === 'array'
          
          // Campos de seção (null) ocupam linha inteira
          if (isNullType) {
            return (
              <div key={prop.name} className="col-span-full">
                {prop.content}
              </div>
            )
          }
          // Arrays ocupam linha inteira
          if (isArray || prop.name === 'pagamentos') {
            return (
              <div key={prop.name} className="col-span-full">
                {prop.content}
              </div>
            )
          }
          // Textarea, richtext e observações ocupam linha inteira
          if (prop.name === 'observacoes' || prop.name === 'usuario_sistema' || isRichText || isTextarea || prop.name.includes('descricao')) {
            return (
              <div key={prop.name} className="col-span-full">
                {prop.content}
              </div>
            )
          }
          return (
            <div key={prop.name}>
              {prop.content}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Widgets customizados para @rjsf
const widgets: RegistryWidgetsType = {
  TextWidget,
  SelectWidget,
  TextareaWidget,
  DateWidget,
  EmailWidget,
  RadioWidget,
  date: DateWidget,
  email: EmailWidget,
  radio: RadioWidget,
  richtext: RichTextWidget,
  cpf: CPFWidget,
  'fornecedor-selector': FornecedorSelectorWidget,
  'cargo-selector': CargoSelectorWidget,
  'unidade-selector': UnidadeSelectorWidget,
  'unidade-setor-selector': UnidadeSelectorWidget, // para compatibilidade, ambos usam o mesmo widget de unidade
  'user-selector': UserSelectorWidget,
}

// Template customizado para itens do array (cada elemento individual)
const CustomArrayFieldItemTemplate: React.FC<ArrayFieldItemTemplateProps> = (props) => {
  const { children, disabled, readonly, buttonsProps, index } = props
  const { hasRemove, onRemoveItem } = buttonsProps
  
  return (
    <div 
      className={cn(
        'relative rounded-lg p-4 transition-all',
        'border-2 border-gray-200 dark:border-neutral-700',
        'bg-gradient-to-br from-white to-gray-50 dark:from-neutral-800 dark:to-neutral-800/50',
        'hover:border-blue-300 dark:hover:border-blue-700',
        'hover:shadow-sm'
      )}
    >
      {/* Badge do número do item */}
      <div className="absolute -top-3 left-3">
        <span className={cn(
          'inline-flex items-center justify-center',
          'px-2.5 py-0.5 text-xs font-semibold rounded-full',
          'bg-blue-100 dark:bg-blue-900/50',
          'text-blue-700 dark:text-blue-300',
          'border border-blue-200 dark:border-blue-800'
        )}>
          Item {index + 1}
        </span>
      </div>
      
      {/* Renderizar os campos do item */}
      <div className="mt-2">
        {children}
      </div>
      
      {/* Botão de remover */}
      {hasRemove && (
        <div className="flex justify-end mt-4 pt-3 border-t border-gray-200 dark:border-neutral-700">
          <button
            type="button"
            onClick={onRemoveItem}
            disabled={disabled || readonly}
            className={cn(
              'inline-flex items-center gap-1.5',
              'px-3 py-1.5 text-sm font-medium rounded-lg',
              'bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30',
              'text-red-600 dark:text-red-400',
              'transition-all duration-200',
              'border border-red-200 dark:border-red-800',
              'hover:shadow-sm hover:scale-105',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100'
            )}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Remover
          </button>
        </div>
      )}
    </div>
  )
}

// Template customizado para arrays (container de todos os itens)
const CustomArrayFieldTemplate: React.FC<ArrayFieldTemplateProps> = (props) => {
  const { items, canAdd, onAddClick } = props
  
  return (
    <div className="array-field-template space-y-4">
      {/* Header do array com título e contador */}
      {/* {title && (
        <div className="flex items-center justify-between gap-3 pb-3 border-b-2 border-gray-200 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <label className={cn(labelClasses, 'mb-0')}>
              {title}
              {required && <span className={requiredClasses}>*</span>}
            </label>
            {items.length > 0 && (
              <span className={cn(
                'inline-flex items-center justify-center',
                'min-w-[1.5rem] h-6 px-2 text-xs font-bold rounded-full',
                'bg-blue-100 dark:bg-blue-900/50',
                'text-blue-700 dark:text-blue-300',
                'border border-blue-200 dark:border-blue-800'
              )}>
                {items.length}
              </span>
            )}
          </div>
        </div>
      )} */}
      
      {/* Lista de itens */}
      <div className="space-y-4">
        {items.length === 0 && (
          <div className={cn(
            'text-center py-8 px-4 rounded-lg',
            'bg-gray-50 dark:bg-neutral-800/50',
            'border-2 border-dashed border-gray-300 dark:border-neutral-600'
          )}>
            <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Nenhum pagamento adicionado
            </p>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Clique no botão abaixo para adicionar
            </p>
          </div>
        )}
        
        {/* Renderizar os itens */}
        {items.map((element) => (
          <div key={element.key}>
            {element}
          </div>
        ))}
      </div>
      
      {/* Botão de adicionar */}
      {canAdd && (
        <button
          type="button"
          onClick={onAddClick}
          className={cn(
            'w-full px-4 py-3 text-sm font-medium rounded-lg',
            'bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-900/30',
            'hover:from-blue-100 hover:to-blue-200 dark:hover:from-blue-900/30 dark:hover:to-blue-900/50',
            'text-blue-600 dark:text-blue-400',
            'transition-all duration-200',
            'border-2 border-blue-200 dark:border-blue-800',
            'hover:border-blue-300 dark:hover:border-blue-700',
            'hover:shadow-md hover:scale-[1.02]',
            'active:scale-[0.98]',
            'flex items-center justify-center gap-2'
          )}
        >
          <svg 
            className="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2.5} 
              d="M12 4v16m8-8H4" 
            />
          </svg>
          <span className="font-semibold">Adicionar Pagamento</span>
        </button>
      )}
    </div>
  )
}

// Templates customizados
const templates = {
  FieldTemplate: CustomFieldTemplate,
  ObjectFieldTemplate: CustomObjectFieldTemplate,
  ArrayFieldTemplate: CustomArrayFieldTemplate,
  ArrayFieldItemTemplate: CustomArrayFieldItemTemplate,
}

interface TemplateFormRendererProps {
  /** Schema do template */
  templateSchema: TemplateSchema
  /** Valores atuais do formulário */
  formData?: Record<string, unknown>
  /** Callback quando os dados mudam */
  onChange: (data: Record<string, unknown>) => void
  /** Callback quando o formulário é submetido */
  onSubmit?: (data: Record<string, unknown>) => void
  /** Se o formulário está desabilitado */
  disabled?: boolean
  /** Se deve mostrar apenas visualização (sem edição) */
  readonly?: boolean
  /** Classes CSS adicionais */
  className?: string
  /** Se deve mostrar o botão de submit */
  showSubmitButton?: boolean
  /** Se deve mostrar erros de validação em tempo real */
  liveValidate?: boolean
}

/**
 * Componente que renderiza formulários dinâmicos baseados em JSON Schema
 */
export const TemplateFormRenderer: React.FC<TemplateFormRendererProps> = ({
  templateSchema,
  formData = {},
  onChange,
  onSubmit,
  disabled = false,
  readonly = false,
  className,
  showSubmitButton = false,
  liveValidate = false,
}) => {
  // Memoizar o schema e uiSchema
  const schema = useMemo<RJSFSchema>(() => {
    // Remove título/descrição do nível raiz para evitar duplicação com o header da página
    const { title: _omitTitle, description: _omitDescription, ...rest } = templateSchema.schema as RJSFSchema
    return rest
  }, [templateSchema.schema])
  const uiSchema = useMemo<UiSchema>(() => templateSchema.uiSchema || {}, [templateSchema.uiSchema])

  // Handler de mudança
  const handleChange = useCallback(
    (data: { formData?: Record<string, unknown> }) => {
      if (data.formData) {
        onChange(data.formData)
      }
    },
    [onChange]
  )

  // Handler de submit
  const handleSubmit = useCallback(
    (data: { formData?: Record<string, unknown> }) => {
      if (onSubmit && data.formData) {
        onSubmit(data.formData)
      }
    },
    [onSubmit]
  )

  return (
    <div className={cn('template-form-renderer', className)}>
      <Form
        schema={schema}
        uiSchema={{
          ...uiSchema,
          'ui:submitButtonOptions': {
            norender: !showSubmitButton,
          },
        }}
        formData={formData}
        onChange={handleChange}
        onSubmit={handleSubmit}
        validator={validator}
        widgets={widgets}
        templates={templates}
        disabled={disabled}
        readonly={readonly}
        liveValidate={liveValidate}
        showErrorList={false}
        tagName="div" // Evita criar <form> aninhado quando usado dentro de outro form
      />
    </div>
  )
}

export default TemplateFormRenderer
